import React from 'react';
import {
    Grid,
    GridItem,
    HStack,
    VStack,
    Text,
    Avatar,
    IconButton,
    Icon,
    Button,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    MenuDivider,
    Box,
    Textarea,
    Badge,
    Divider,
    ButtonGroup,
    chakra,
    StackDivider,
    Popover,
    PopoverContent,
    PopoverTrigger,
    Tooltip,
    PopoverHeader,
    PopoverBody,
    PopoverFooter,
    Switch,
    Input,
    Select,
} from '@chakra-ui/react';
import { MdSettings, MdOutlineAlternateEmail, MdOutlineClose } from 'react-icons/md';
import { BsArrowRightShort } from 'react-icons/bs';
import { RiAttachment2 } from 'react-icons/ri';
import TextareaAutosize from 'react-textarea-autosize';
import { IntegrationViewBaseConfig } from '../../../../base';
import {
    BaseContextProps,
    ThreadOptionContainerProps,
    ThreadOptionViewProps,
} from '../../../../../chat/composer';
import {
    ComposerViewProps,
    ThreadContextItemComponents,
    ThreadContextContainerProps,
    ThreadContextViewProps,
} from '../../../../../chat';
import { ComposerContainerProps } from '../../../../../chat';
import { CollectionContextContainerProps } from '../../../../../chat/composer/context/select/option/collection';
import { SolutionContextContainerProps } from '../../../../../chat/composer/context/select/option/solution';
import { ThreadContextItemContainerProps } from '../../../../../chat';

const ChakraTextareaAutosize = chakra(TextareaAutosize, {
    baseStyle: {},
});

export function createThreadCommonComposerView(
    config: IntegrationViewBaseConfig & {
        Container: {
            ThreadOption: React.FC<ThreadOptionContainerProps>;
            ThreadContext: React.FC<ThreadContextContainerProps>;
            Context: {
                Select: {
                    Collection: React.FC<CollectionContextContainerProps>;
                    Solution: React.FC<SolutionContextContainerProps>;
                };
                Item: React.FC<ThreadContextItemContainerProps>;
            };
            Composer: React.FC<ComposerContainerProps>;
        };
    }
): React.FC<ComposerViewProps> {
    const {
        Container: {
            ThreadOption: ThreadOptionContainer,
            ThreadContext: ThreadContextContainer,
            Composer: ComposerContainer,
            Context: {
                Select: {
                    Collection: CollectionContextContainer,
                    Solution: SolutionContextContainer,
                },
                Item: ContextItemContainer,
            },
        },
    } = config;

    const OptionForm: React.FC<ThreadOptionViewProps> = (props) => {
        return (
            <VStack w="full" spacing={0} bg="whiteAlpha.200">
                <PopoverHeader w="full" px={3} py={2}>
                    <Text color="whiteAlpha.800" fontWeight="semibold">
                        Options
                    </Text>
                </PopoverHeader>
                <PopoverBody w="full" p={3}>
                    <VStack
                        align="start"
                        w="full"
                        divider={<StackDivider borderColor="whiteAlpha.100" />}
                        spacing={4}
                    >
                        {props.fields.map((field) => (
                            <HStack
                                key={field.id}
                                w="full"
                                justify="space-between"
                                spacing={6}
                            >
                                <VStack align="start" spacing={0.5}>
                                    <Text color="whiteAlpha.800" fontWeight="semibold">
                                        {field.label}
                                    </Text>
                                    <Text color="whiteAlpha.600">
                                        {field.description}
                                    </Text>
                                </VStack>
                                <HStack justify="end">
                                    {field.input.kind === 'switch' && (
                                        <Switch {...field.input.switch} />
                                    )}
                                    {field.input.kind === 'select' && (
                                        <Select
                                            {...field.input.select}
                                            size="sm"
                                            minW="7rem"
                                            noOfLines={1}
                                            _focus={{ outline: 'none' }}
                                            _focusVisible={{ outline: 'none' }}
                                        >
                                            {field.input.options.map((option) => (
                                                <option
                                                    key={option.value}
                                                    value={option.value}
                                                >
                                                    {option.label}
                                                </option>
                                            ))}
                                        </Select>
                                    )}
                                </HStack>
                            </HStack>
                        ))}
                    </VStack>
                </PopoverBody>
                <PopoverFooter w="full">
                    <HStack w="full" justify="end">
                        <Button {...props.submit.button} size="sm" colorScheme="green">
                            {props.submit.label}
                        </Button>
                    </HStack>
                </PopoverFooter>
            </VStack>
        );
    };

    const ContextSelect: React.FC<BaseContextProps> = (props) => {
        return (
            <VStack w="full" spacing={0} bg="whiteAlpha.200">
                <PopoverHeader w="full" px={3} py={2}>
                    <HStack w="full" justify="space-between" align="center">
                        <IconButton
                            size="xs"
                            variant="ghost"
                            aria-label="Back"
                            onClick={props.onBack}
                            icon={<Text fontSize="sm">←</Text>}
                            color="whiteAlpha.600"
                            _hover={{
                                color: 'whiteAlpha.900',
                                bg: 'whiteAlpha.50',
                            }}
                        />
                        <Text
                            color="whiteAlpha.800"
                            fontWeight="semibold"
                            flex={1}
                            textAlign="center"
                        >
                            {props.header.label}
                        </Text>
                        <Box w={6} /> {/* Spacer for centering */}
                    </HStack>
                </PopoverHeader>
                <PopoverBody w="full" p={3}>
                    <VStack align="start" w="full" spacing={3}>
                        <Input {...props.search.input} size="sm" />
                        <VStack w="full" spacing={1}>
                            {props.items.length === 0 ? (
                                <Text color="whiteAlpha.600" fontSize="sm" py={4}>
                                    Loading solutions...
                                </Text>
                            ) : (
                                props.items.map((item) => (
                                    <Button
                                        key={item.id}
                                        {...item.button}
                                        w="full"
                                        variant="ghost"
                                        justifyContent="flex-start"
                                        fontSize="sm"
                                        color="whiteAlpha.800"
                                        _hover={{
                                            bg: 'whiteAlpha.50',
                                            color: 'whiteAlpha.900',
                                        }}
                                    >
                                        {item.label}
                                    </Button>
                                ))
                            )}
                        </VStack>
                    </VStack>
                </PopoverBody>
            </VStack>
        );
    };

    const ContextForm: React.FC<ThreadContextViewProps> = (props) => {
        // Render nested view if we're not on the main screen
        if (props.current?.kind === 'solution') {
            return <SolutionContextContainer as={ContextSelect} {...props.current} />;
        }
        if (props.current?.kind === 'collection') {
            return <CollectionContextContainer as={ContextSelect} {...props.current} />;
        }
        // Render main options screen
        return (
            <VStack w="full" spacing={0} bg="whiteAlpha.200">
                <PopoverHeader w="full" px={3} py={2}>
                    <Text color="whiteAlpha.800" fontWeight="semibold">
                        Add Context
                    </Text>
                </PopoverHeader>
                <PopoverBody w="full" p={0}>
                    <VStack align="start" w="full" spacing={0}>
                        {props.options.map((item) => (
                            <Button
                                key={item.value}
                                w="full"
                                onClick={() => props.onAddContextType?.(item.value)}
                                variant="ghost"
                                borderRadius="none"
                                py={3}
                                px={3}
                                h="auto"
                                fontSize="sm"
                                color="whiteAlpha.800"
                                justifyContent="flex-start"
                                _hover={{
                                    bg: 'whiteAlpha.50',
                                    color: 'whiteAlpha.900',
                                }}
                            >
                                <VStack align="start" spacing={0.5} w="full">
                                    <Text fontWeight="medium">{item.label}</Text>
                                    <Text color="whiteAlpha.600" fontSize="xs">
                                        {item.description}
                                    </Text>
                                </VStack>
                            </Button>
                        ))}
                    </VStack>
                </PopoverBody>
            </VStack>
        );
    };

    const ContextItemComponent: ThreadContextItemComponents = {
        Solution(props) {
            return (
                <Popover trigger="hover" placement="top">
                    <PopoverTrigger>
                        <Badge
                            size="xs"
                            px={2}
                            py={0.5}
                            borderWidth={1}
                            borderColor="whiteAlpha.200"
                            bg="none"
                            borderRadius="md"
                            color="whiteAlpha.600"
                            display="flex"
                            alignItems="center"
                            gap={2}
                            _hover={{
                                bg: 'whiteAlpha.50',
                            }}
                        >
                            <Icon
                                as={props.item.Icon}
                                fontSize="sm"
                                color="whiteAlpha.600"
                            />
                            <Text noOfLines={1} maxW="12rem" userSelect="none">
                                {props.item.label}
                            </Text>
                            <Icon
                                as={MdOutlineClose}
                                onClick={props.item.remove.button.onClick}
                                fontSize="sm"
                                color="whiteAlpha.400"
                                cursor="pointer"
                                _hover={{
                                    color: 'whiteAlpha.500',
                                }}
                            />
                        </Badge>
                    </PopoverTrigger>
                    <PopoverContent
                        bg="black"
                        color="whiteAlpha.900"
                        overflow="hidden"
                        maxW="20rem"
                        w="full"
                        _focus={{ outline: 'none' }}
                    >
                        <PopoverBody
                            as={VStack}
                            spacing={2}
                            p={0}
                            bg="whiteAlpha.200"
                            py={2}
                            fontSize="sm"
                            divider={
                                <StackDivider
                                    borderWidth={1}
                                    borderColor="whiteAlpha.100"
                                />
                            }
                        >
                            <HStack w="full" justify="space-between" px={3}>
                                <HStack
                                    color="whiteAlpha.800"
                                    spacing={2}
                                    fontWeight="medium"
                                >
                                    <Icon as={props.item.Icon} />
                                    <Text>{props.item.label}</Text>
                                </HStack>
                                <Text
                                    color="whiteAlpha.400"
                                    fontSize="xs"
                                    textTransform="uppercase"
                                    fontWeight="bold"
                                >
                                    {props.item.type.label}
                                </Text>
                            </HStack>
                            <VStack color="whiteAlpha.600" px={3}>
                                <Text>{props.item.description}</Text>
                            </VStack>
                        </PopoverBody>
                    </PopoverContent>
                </Popover>
            );
        },
    };

    return (props) => {
        const composerRef = React.useRef<HTMLTextAreaElement>(null);

        // Auto-focus logic
        React.useEffect(() => {
            if (composerRef.current) {
                composerRef.current.focus();
            }
        }, []);

        return (
            <VStack
                w="full"
                align="start"
                spacing={3}
                p={4}
                borderRadius="xl"
                bg="whiteAlpha.50"
                position="relative"
            >
                <ChakraTextareaAutosize
                    ref={composerRef}
                    {...props.textarea}
                    placeholder={props.textarea.placeholder || 'Type a message...'}
                    w="full"
                    lineHeight={1.25}
                    minRows={1}
                    maxRows={8}
                    border="none"
                    bg="none"
                    resize="none"
                    color="whiteAlpha.800"
                    sx={{ caretColor: 'whiteAlpha.800' }}
                    _disabled={{
                        color: 'whiteAlpha.500',
                        cursor: 'default',
                    }}
                    _placeholder={{
                        color: 'whiteAlpha.400',
                    }}
                    _focus={{
                        outline: 'none',
                    }}
                    _focusVisible={{
                        outline: 'none',
                    }}
                />
                <HStack w="full" justify="space-between" align="end">
                    <HStack spacing={3}>
                        <HStack spacing={1}>
                            <Tooltip
                                placement="top"
                                bg="gray.50"
                                color="gray.900"
                                py={1}
                                px={2}
                                borderRadius="md"
                                openDelay={500}
                                label="Add file"
                            >
                                <IconButton
                                    bg="none"
                                    _hover={{
                                        bg: 'whiteAlpha.50',
                                    }}
                                    color="whiteAlpha.600"
                                    aria-label="Add file"
                                    size="sm"
                                    icon={<Icon fontSize="lg" as={RiAttachment2} />}
                                />
                            </Tooltip>
                            <Tooltip
                                placement="top"
                                bg="gray.50"
                                color="gray.900"
                                py={1}
                                px={2}
                                borderRadius="md"
                                openDelay={500}
                                label="Add context"
                                closeOnClick={true}
                                shouldWrapChildren={true}
                            >
                                <Popover
                                    {...props.context.popover}
                                    lazyBehavior="unmount"
                                    isLazy={true}
                                >
                                    <PopoverTrigger>
                                        <IconButton
                                            bg="none"
                                            _hover={{
                                                bg: 'whiteAlpha.50',
                                            }}
                                            size="sm"
                                            color="whiteAlpha.600"
                                            aria-label="Add context"
                                            icon={
                                                <Icon
                                                    fontSize="lg"
                                                    as={MdOutlineAlternateEmail}
                                                />
                                            }
                                        />
                                    </PopoverTrigger>
                                    <PopoverContent
                                        w="24rem"
                                        maxH="20rem"
                                        bg="black"
                                        p={0}
                                        overflow="auto"
                                        fontSize="sm"
                                        _focus={{
                                            outline: 'none',
                                        }}
                                        _focusVisible={{
                                            outline: 'none',
                                        }}
                                        sx={{
                                            '&::-webkit-scrollbar': {
                                                width: '8px',
                                            },
                                            '&::-webkit-scrollbar-track': {
                                                background: 'transparent',
                                            },
                                            '&::-webkit-scrollbar-thumb': {
                                                background: 'rgba(255,255,255,0.3)',
                                                borderRadius: '4px',
                                                '&:hover': {
                                                    background: 'rgba(255,255,255,0.5)',
                                                },
                                            },
                                        }}
                                    >
                                        <ThreadContextContainer
                                            as={ContextForm}
                                            {...props.context.form}
                                        />
                                    </PopoverContent>
                                </Popover>
                            </Tooltip>
                            <Tooltip
                                placement="top"
                                bg="gray.50"
                                color="gray.900"
                                py={1}
                                px={2}
                                borderRadius="md"
                                openDelay={500}
                                closeOnClick={true}
                                label="Options"
                            >
                                <Popover
                                    {...props.option.popover}
                                    lazyBehavior="unmount"
                                    isLazy={true}
                                >
                                    <PopoverTrigger>
                                        <IconButton
                                            bg="none"
                                            _hover={{
                                                bg: 'whiteAlpha.50',
                                            }}
                                            size="sm"
                                            color="whiteAlpha.600"
                                            aria-label="Options"
                                            icon={<Icon fontSize="lg" as={MdSettings} />}
                                        />
                                    </PopoverTrigger>
                                    <PopoverContent
                                        w="24rem"
                                        maxH="20rem"
                                        bg="black"
                                        p={0}
                                        overflow="auto"
                                        fontSize="sm"
                                        _focus={{
                                            outline: 'none',
                                        }}
                                        _focusVisible={{
                                            outline: 'none',
                                        }}
                                        sx={{
                                            '&::-webkit-scrollbar': {
                                                width: '8px',
                                            },
                                            '&::-webkit-scrollbar-track': {
                                                background: 'transparent',
                                            },
                                            '&::-webkit-scrollbar-thumb': {
                                                background: 'rgba(255,255,255,0.3)',
                                                borderRadius: '4px',
                                                '&:hover': {
                                                    background: 'rgba(255,255,255,0.5)',
                                                },
                                            },
                                        }}
                                    >
                                        <ThreadOptionContainer
                                            as={OptionForm}
                                            {...props.option.form}
                                        />
                                    </PopoverContent>
                                </Popover>
                            </Tooltip>
                        </HStack>
                        {/* Current context items */}
                        {props.context.selected.length > 0 && (
                            <VStack align="start" w="full" spacing={2}>
                                <HStack spacing={2} wrap="wrap">
                                    {props.context.selected.map((item) => (
                                        <ContextItemContainer
                                            key={item.id}
                                            as={ContextItemComponent}
                                            {...item}
                                        />
                                    ))}
                                </HStack>
                            </VStack>
                        )}
                    </HStack>
                    <HStack>
                        <IconButton
                            aria-label="Send message"
                            icon={<Icon fontSize="2xl" as={BsArrowRightShort} />}
                            size="sm"
                            borderRadius="full"
                            bg="white"
                            color="black"
                            {...props.submit.button}
                            _hover={{
                                bg: 'whiteAlpha.900',
                            }}
                            _disabled={{
                                opacity: 0.3,
                                cursor: 'default',
                            }}
                        />
                    </HStack>
                </HStack>
            </VStack>
        );
    };
}
